import type { IconName } from '@noxo/ui/icon';

export type DemoJourneyPersona = 'returning' | 'new' | 'inReview' | 'guest';

export interface DemoJourney {
  readonly icon: IconName;
  readonly label: string;
  readonly caption: string;
  readonly persona: DemoJourneyPersona;
  readonly path: string;
  readonly later?: string;
}

export const demoJourneys = (base: string): readonly DemoJourney[] => [
  {
    icon: 'lock',
    label: 'Sign in with 2FA',
    caption: 'Email, password, authenticator code',
    persona: 'guest',
    path: `${base}/sign-in`,
  },
  {
    icon: 'envelope',
    label: 'Register & activate',
    caption: 'Email → inbox → password',
    persona: 'guest',
    path: `${base}/sign-up`,
  },
  {
    icon: 'list-ul',
    label: 'Onboarding checklist',
    caption: 'Five steps to the first investment',
    persona: 'new',
    path: `${base}/onboarding`,
  },
  {
    icon: 'address-card',
    label: 'Personal details',
    caption: 'Contact, address, tax, income',
    persona: 'new',
    path: `${base}/onboarding/personal-details`,
  },
  {
    icon: 'clipboard-check',
    label: 'Suitability quiz',
    caption: 'Categorisation, knowledge, loss test',
    persona: 'new',
    path: `${base}/onboarding/suitability`,
  },
  {
    icon: 'fingerprint',
    label: 'Identity verification',
    caption: 'Document, selfie, review',
    persona: 'new',
    path: `${base}/onboarding/identity`,
  },
  {
    icon: 'spinner-third',
    label: 'KYC decision',
    caption: 'Pending review → approve or reject',
    persona: 'inReview',
    path: `${base}/onboarding/identity`,
  },
  {
    icon: 'arrow-down-to-arc',
    label: 'First deposit',
    caption: 'Bank transfer with reference',
    persona: 'returning',
    path: `${base}/account/wallet?dialog=deposit`,
  },
  {
    icon: 'cart-shopping',
    label: 'Invest in a project',
    caption: 'Amount → review & sign → shares',
    persona: 'returning',
    path: `${base}/market/projects/1?invest=500`,
  },
  {
    icon: 'arrow-rotate-left',
    label: 'Sell a stake',
    caption: 'NOXO buys back · fee by holding period',
    persona: 'returning',
    path: `${base}/account/portfolio/holdings/2?dialog=sell&projectId=2`,
  },
  {
    icon: 'arrow-up-from-arc',
    label: 'Withdraw',
    caption: 'Account, amount, 2FA, approval',
    persona: 'returning',
    path: `${base}/account/wallet?dialog=withdraw`,
  },
  {
    icon: 'arrow-right-arrow-left',
    label: 'Exchange currency',
    caption: 'EUR → USD at interbank rate',
    persona: 'returning',
    path: `${base}/account/wallet?dialog=exchange`,
  },
  {
    icon: 'shield-keyhole',
    label: 'Enable 2FA',
    caption: 'Authenticator setup + backup codes',
    persona: 'returning',
    path: `${base}/account/security-and-privacy?dialog=two-factor`,
  },
  {
    icon: 'building-columns',
    label: 'Bank accounts',
    caption: 'Add and verify payout accounts',
    persona: 'returning',
    path: `${base}/account/bank-accounts`,
  },
  {
    icon: 'file-lines',
    label: 'Documents',
    caption: 'Agreements and tax documents',
    persona: 'returning',
    path: `${base}/account/documents`,
  },
  {
    icon: 'comment-lines',
    label: 'File a complaint',
    caption: 'Support with a tracked reference',
    persona: 'returning',
    path: `${base}/account?dialog=complaint`,
  },
  {
    icon: 'building-columns',
    label: 'Move to the EU entity',
    caption: 'One-time terms acceptance after licensing',
    persona: 'returning',
    path: `${base}/account?dialog=entity`,
    later: 'T9 · EU entity',
  },
  {
    icon: 'lock',
    label: 'Locked-term holding',
    caption: 'Sell is blocked until the term ends',
    persona: 'returning',
    path: `${base}/account/portfolio/holdings/4?dialog=sell&projectId=4`,
  },
  {
    icon: 'flag',
    label: 'Exited property',
    caption: 'Realised return after the sale',
    persona: 'returning',
    path: `${base}/account/portfolio/holdings/7`,
  },
  {
    icon: 'arrow-rotate-left',
    label: 'Shares available again',
    caption: 'Funded project with a resold stake to buy',
    persona: 'returning',
    path: `${base}/market`,
    later: 'T3 · Resale',
  },
];

export const demoScreens = (base: string): readonly DemoJourney[] => [
  {
    icon: 'chart-pie',
    label: 'Portfolio',
    caption: 'Holdings, value, payouts',
    persona: 'returning',
    path: `${base}/account/portfolio`,
  },
  {
    icon: 'house',
    label: 'Holding detail',
    caption: 'Position, payouts, documents',
    persona: 'returning',
    path: `${base}/account/portfolio/holdings/2`,
  },
  {
    icon: 'store',
    label: 'Market',
    caption: 'Open projects and filters',
    persona: 'returning',
    path: `${base}/market`,
  },
  {
    icon: 'building',
    label: 'Project detail',
    caption: 'Story, numbers, risks',
    persona: 'returning',
    path: `${base}/market/projects/1`,
  },
  {
    icon: 'wallet',
    label: 'Wallet',
    caption: 'Balances and transactions',
    persona: 'returning',
    path: `${base}/account/wallet`,
  },
  { icon: 'user', label: 'Account', caption: 'Hub with every setting', persona: 'returning', path: `${base}/account` },
  {
    icon: 'address-card',
    label: 'Personal details (account)',
    caption: 'Contact, address, tax, income',
    persona: 'returning',
    path: `${base}/account/personal-details`,
  },
  {
    icon: 'sliders',
    label: 'Preferences',
    caption: 'Language, currency, appearance',
    persona: 'returning',
    path: `${base}/account/settings`,
  },
  {
    icon: 'bell',
    label: 'Notification preferences',
    caption: 'What we send and where',
    persona: 'returning',
    path: `${base}/account/settings/notifications`,
  },
  {
    icon: 'shield',
    label: 'Security and privacy',
    caption: 'Password, 2FA, devices',
    persona: 'returning',
    path: `${base}/account/security-and-privacy`,
  },
  {
    icon: 'user-shield',
    label: 'Privacy requests',
    caption: 'Export, restriction, erasure',
    persona: 'returning',
    path: `${base}/account/privacy`,
  },
  {
    icon: 'id-badge',
    label: 'Investor profile',
    caption: 'Category and net worth',
    persona: 'returning',
    path: `${base}/account/investor-profile`,
  },
  {
    icon: 'building-columns',
    label: 'Your NOXO entity',
    caption: 'Operator, regulator, terms',
    persona: 'returning',
    path: `${base}/account?dialog=entity`,
  },
  {
    icon: 'inbox',
    label: 'Notifications',
    caption: 'History of messages',
    persona: 'returning',
    path: `${base}/account/notifications`,
  },
  {
    icon: 'lock',
    label: 'Lock your account',
    caption: 'Emergency panic freeze',
    persona: 'guest',
    path: '/security/panic',
  },
  { icon: 'palette', label: 'Design system', caption: 'NOXO components', persona: 'guest', path: '/showcase/noxo' },
];
